import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildClassMemberNumberMap } from '../../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import type { ClassMemberRow, ClassMemberSeatPosition } from '../../utils/classMembersStorage';
import { clientRectToLayoutRect } from '../../utils/pageZoom';
import {
  CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID,
  CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME,
  createSeatLayoutPresetId,
  ensureSeatLayoutPresetsState,
  loadClassMemberSeatLayoutPresets,
  saveClassMemberSeatLayoutPresets,
  seatsMapToRecord,
  seatsRecordToMap,
  type ClassMemberSeatLayoutPreset,
  type ClassMemberSeatLayoutPresetsState,
} from '../../utils/classMemberSeatLayoutPresetsStorage';

const GRID_ROWS = 16;
const GRID_COLS = 16;
const STUDENT_BLOCK_SIZE = 2;

function rotateSeatCell(row: number, col: number): ClassMemberSeatPosition {
  return {
    row: GRID_ROWS - 1 - row,
    col: GRID_COLS - 1 - col,
  };
}

function rotateSeatPosition(position: ClassMemberSeatPosition): ClassMemberSeatPosition {
  return {
    row: GRID_ROWS - STUDENT_BLOCK_SIZE - position.row,
    col: GRID_COLS - STUDENT_BLOCK_SIZE - position.col,
  };
}

export type ClassMemberSeatAssignmentModalProps = {
  open: boolean;
  onClose: () => void;
  groupName: string;
  members: ClassMemberRow[];
  workspaceId: string;
  gradeId: string;
  groupId: string;
  onSave: (updatedMembers: ClassMemberRow[]) => void | Promise<void>;
};

function mapsEqual(
  a: Map<string, ClassMemberSeatPosition>,
  b: Map<string, ClassMemberSeatPosition>
): boolean {
  if (a.size !== b.size) return false;
  return Array.from(a.entries()).every(([id, pos]) => {
    const other = b.get(id);
    return Boolean(other && other.row === pos.row && other.col === pos.col);
  });
}

function seatsFromMembers(members: ClassMemberRow[]): Map<string, ClassMemberSeatPosition> {
  const arrangement = new Map<string, ClassMemberSeatPosition>();
  members.forEach((member) => {
    const pos = member.seatPosition;
    if (pos && Number.isFinite(pos.row) && Number.isFinite(pos.col)) {
      arrangement.set(member.id, { row: pos.row, col: pos.col });
    }
  });
  return arrangement;
}

const ClassMemberSeatAssignmentModal: React.FC<ClassMemberSeatAssignmentModalProps> = ({
  open,
  onClose,
  groupName,
  members,
  workspaceId,
  gradeId,
  groupId,
  onSave,
}) => {
  const [seatArrangement, setSeatArrangement] = useState<Map<string, ClassMemberSeatPosition>>(
    () => new Map()
  );
  const [initialSeatArrangement, setInitialSeatArrangement] = useState<
    Map<string, ClassMemberSeatPosition>
  >(() => new Map());
  const [presetsState, setPresetsState] = useState<ClassMemberSeatLayoutPresetsState>(() =>
    ensureSeatLayoutPresetsState(null)
  );
  const [selectedPresetId, setSelectedPresetId] = useState(CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID);
  const [editPresetId, setEditPresetId] = useState<string | null>(null);
  const [editNameDraft, setEditNameDraft] = useState('');
  const [deleteConfirmPresetId, setDeleteConfirmPresetId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createCloneApplied, setCreateCloneApplied] = useState(true);
  const [pickedMemberId, setPickedMemberId] = useState<string | null>(null);
  const [previewPosition, setPreviewPosition] = useState<ClassMemberSeatPosition | null>(null);
  const [hoverSwapId, setHoverSwapId] = useState<string | null>(null);
  const [viewRotated, setViewRotated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRandomized, setIsRandomized] = useState(false);
  const [beforeRandomArrangement, setBeforeRandomArrangement] = useState<
    Map<string, ClassMemberSeatPosition>
  >(() => new Map());
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'center' | 'spread' | 'none'>('none');
  const [targetArrangement, setTargetArrangement] = useState<Map<string, ClassMemberSeatPosition>>(
    () => new Map()
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [saveError, setSaveError] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const editNameInputRef = useRef<HTMLInputElement>(null);
  const createNameInputRef = useRef<HTMLInputElement>(null);
  const skipPresetSeatSyncRef = useRef(false);

  const numberById = useMemo(() => buildClassMemberNumberMap(members), [members]);

  const persistPresets = useCallback(
    (next: ClassMemberSeatLayoutPresetsState) => {
      const normalized = ensureSeatLayoutPresetsState(next);
      setPresetsState(normalized);
      saveClassMemberSeatLayoutPresets({
        workspaceId,
        gradeId,
        groupId,
        state: normalized,
      });
      return normalized;
    },
    [gradeId, groupId, workspaceId]
  );

  const updateSelectedPresetSeats = useCallback(
    (seats: Map<string, ClassMemberSeatPosition>) => {
      setPresetsState((prev) => {
        const record = seatsMapToRecord(seats);
        const nextPresets = prev.presets.map((p) =>
          p.id === selectedPresetId ? { ...p, seats: record, updatedAt: Date.now() } : p
        );
        const next = ensureSeatLayoutPresetsState({ ...prev, presets: nextPresets });
        saveClassMemberSeatLayoutPresets({
          workspaceId,
          gradeId,
          groupId,
          state: next,
        });
        return next;
      });
    },
    [gradeId, groupId, selectedPresetId, workspaceId]
  );

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const grid = gridRef.current;
    const section = sectionRef.current;
    if (!grid || !section) return;
    const sync = () => {
      const width = clientRectToLayoutRect(grid.getBoundingClientRect()).width;
      if (width <= 0) return;
      section.style.setProperty('--cp-seat-grid-width', `${Math.round(width)}px`);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(grid);
    return () => {
      observer.disconnect();
      section.style.removeProperty('--cp-seat-grid-width');
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const fromMembers = seatsFromMembers(members);
    const seed = seatsMapToRecord(fromMembers);
    const loaded = loadClassMemberSeatLayoutPresets({
      workspaceId,
      gradeId,
      groupId,
      seedSeats: seed,
    });
    // 적용 중인 카드 자리를 기준으로 표시 (저장 없이도 적용 기준 유지)
    const applied =
      loaded.presets.find((p) => p.id === loaded.appliedPresetId) ?? loaded.presets[0]!;
    const arrangement = seatsRecordToMap(applied.seats);
    setPresetsState(loaded);
    skipPresetSeatSyncRef.current = true;
    setSeatArrangement(arrangement);
    setInitialSeatArrangement(new Map(arrangement));
    setSelectedPresetId(loaded.appliedPresetId);
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
    setShowCancelConfirm(false);
    setSaveError('');
    setPickedMemberId(null);
    setPreviewPosition(null);
    setHoverSwapId(null);
    setViewRotated(false);
    setIsAnimating(false);
    setAnimationPhase('none');
    setTargetArrangement(new Map());
    setEditPresetId(null);
    setEditNameDraft('');
    setDeleteConfirmPresetId(null);
    setCreateModalOpen(false);
    setCreateName('');
    setCreateCloneApplied(true);
  }, [open, members, workspaceId, gradeId, groupId]);

  useEffect(() => {
    if (!open || skipPresetSeatSyncRef.current) {
      skipPresetSeatSyncRef.current = false;
      return;
    }
    updateSelectedPresetSeats(seatArrangement);
  }, [open, seatArrangement, updateSelectedPresetSeats]);

  useEffect(() => {
    if (!editPresetId || deleteConfirmPresetId) return;
    window.setTimeout(() => {
      editNameInputRef.current?.focus();
      editNameInputRef.current?.select();
    }, 0);
  }, [editPresetId, deleteConfirmPresetId]);

  useEffect(() => {
    if (!createModalOpen) return;
    window.setTimeout(() => createNameInputRef.current?.focus(), 0);
  }, [createModalOpen]);

  const hasChanges = useMemo(
    () => !mapsEqual(seatArrangement, initialSeatArrangement),
    [seatArrangement, initialSeatArrangement]
  );

  const appliedPreset = useMemo(
    () =>
      presetsState.presets.find((p) => p.id === presetsState.appliedPresetId) ??
      presetsState.presets[0] ??
      null,
    [presetsState]
  );

  const editingPreset = useMemo(
    () =>
      editPresetId
        ? presetsState.presets.find((p) => p.id === editPresetId) ?? null
        : null,
    [editPresetId, presetsState]
  );

  const findStudentCoveringCell = useCallback(
    (row: number, col: number, excludeStudentId?: string): string | null => {
      const hit = Array.from(seatArrangement.entries()).find(([studentId, position]) => {
        if (excludeStudentId && studentId === excludeStudentId) return false;
        return (
          row >= position.row &&
          row < position.row + STUDENT_BLOCK_SIZE &&
          col >= position.col &&
          col < position.col + STUDENT_BLOCK_SIZE
        );
      });
      return hit?.[0] ?? null;
    },
    [seatArrangement]
  );

  const findStudentOverlappingBlock = useCallback(
    (row: number, col: number, excludeStudentId?: string): string | null => {
      const hit = Array.from(seatArrangement.entries()).find(([studentId, position]) => {
        if (excludeStudentId && studentId === excludeStudentId) return false;
        return (
          row < position.row + STUDENT_BLOCK_SIZE &&
          row + STUDENT_BLOCK_SIZE > position.row &&
          col < position.col + STUDENT_BLOCK_SIZE &&
          col + STUDENT_BLOCK_SIZE > position.col
        );
      });
      return hit?.[0] ?? null;
    },
    [seatArrangement]
  );

  const clearPick = useCallback(() => {
    setPickedMemberId(null);
    setPreviewPosition(null);
    setHoverSwapId(null);
  }, []);

  const swapSeatMembers = useCallback((aId: string, bId: string) => {
    if (!aId || aId === bId) return;
    setSeatArrangement((prev) => {
      if (!prev.has(aId) && !prev.has(bId)) return prev;
      const next = new Map(prev);
      const aPos = next.get(aId);
      const bPos = next.get(bId);
      if (aPos && bPos) {
        next.set(aId, bPos);
        next.set(bId, aPos);
      } else if (bPos) {
        next.delete(bId);
        next.set(aId, bPos);
      } else if (aPos) {
        next.delete(aId);
        next.set(bId, aPos);
      }
      return next;
    });
    setPickedMemberId(null);
    setPreviewPosition(null);
    setHoverSwapId(null);
  }, []);

  const cellFromPointer = (clientX: number, clientY: number): ClassMemberSeatPosition | null => {
    const grid = gridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cellWidth = grid.offsetWidth / GRID_COLS;
    const cellHeight = grid.offsetHeight / GRID_ROWS;
    if (x < 0 || y < 0 || x >= GRID_COLS * cellWidth || y >= GRID_ROWS * cellHeight) return null;
    return { row: Math.floor(y / cellHeight), col: Math.floor(x / cellWidth) };
  };

  const describeDrop = useCallback(
    (
      memberId: string,
      row: number,
      col: number
    ):
      | { kind: 'self' }
      | { kind: 'swap'; otherId: string; position: ClassMemberSeatPosition }
      | { kind: 'move'; position: ClassMemberSeatPosition } => {
      const own = seatArrangement.get(memberId);
      if (
        own &&
        row >= own.row &&
        row < own.row + STUDENT_BLOCK_SIZE &&
        col >= own.col &&
        col < own.col + STUDENT_BLOCK_SIZE
      ) {
        return { kind: 'self' };
      }
      const covering = findStudentCoveringCell(row, col, memberId);
      if (covering) {
        const position = seatArrangement.get(covering);
        if (position) return { kind: 'swap', otherId: covering, position };
      }
      const origin = {
        row: Math.max(0, Math.min(row, GRID_ROWS - STUDENT_BLOCK_SIZE)),
        col: Math.max(0, Math.min(col, GRID_COLS - STUDENT_BLOCK_SIZE)),
      };
      const overlapping = findStudentOverlappingBlock(origin.row, origin.col, memberId);
      if (overlapping) {
        const position = seatArrangement.get(overlapping);
        if (position) return { kind: 'swap', otherId: overlapping, position };
      }
      return { kind: 'move', position: origin };
    },
    [findStudentCoveringCell, findStudentOverlappingBlock, seatArrangement]
  );

  const placePickedAt = (row: number, col: number) => {
    if (!pickedMemberId || isAnimating) return;
    const drop = describeDrop(pickedMemberId, row, col);
    if (drop.kind === 'self') {
      clearPick();
      return;
    }
    if (drop.kind === 'swap') {
      swapSeatMembers(pickedMemberId, drop.otherId);
      return;
    }
    setSeatArrangement((prev) => {
      const next = new Map(prev);
      next.set(pickedMemberId, drop.position);
      return next;
    });
    clearPick();
  };

  const handleMemberClick = (memberId: string) => {
    if (isAnimating) return;
    if (!pickedMemberId) {
      setPickedMemberId(memberId);
      return;
    }
    if (pickedMemberId === memberId) {
      clearPick();
      return;
    }
    if (!seatArrangement.has(pickedMemberId) && !seatArrangement.has(memberId)) {
      setPickedMemberId(memberId);
      return;
    }
    swapSeatMembers(pickedMemberId, memberId);
  };

  const handleGridMouseMove = (e: React.MouseEvent) => {
    if (!pickedMemberId || isAnimating) return;
    const visual = cellFromPointer(e.clientX, e.clientY);
    if (!visual) {
      setPreviewPosition(null);
      setHoverSwapId(null);
      return;
    }
    const cell = viewRotated ? rotateSeatCell(visual.row, visual.col) : visual;
    const drop = describeDrop(pickedMemberId, cell.row, cell.col);
    if (drop.kind === 'self') {
      setPreviewPosition(null);
      setHoverSwapId(null);
      return;
    }
    if (drop.kind === 'swap') {
      setPreviewPosition(drop.position);
      setHoverSwapId(drop.otherId);
      return;
    }
    setPreviewPosition(drop.position);
    setHoverSwapId(null);
  };

  const handleGridClick = (e: React.MouseEvent) => {
    if (!pickedMemberId || isAnimating) return;
    if ((e.target as HTMLElement).closest('.cp-class-seat-placed-block')) return;
    const visual = cellFromPointer(e.clientX, e.clientY);
    if (!visual) return;
    const cell = viewRotated ? rotateSeatCell(visual.row, visual.col) : visual;
    placePickedAt(cell.row, cell.col);
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelConfirm(true);
      return;
    }
    onClose();
  };

  const getGridCenter = (): ClassMemberSeatPosition => ({
    row: Math.floor(GRID_ROWS / 2) - 1,
    col: Math.floor(GRID_COLS / 2) - 1,
  });

  const animateToArrangement = (newTarget: Map<string, ClassMemberSeatPosition>) => {
    setIsAnimating(true);
    setTargetArrangement(newTarget);
    setAnimationPhase('center');
    window.setTimeout(() => {
      setAnimationPhase('spread');
      window.setTimeout(() => {
        setSeatArrangement(newTarget);
        setAnimationPhase('none');
        setIsAnimating(false);
        setTargetArrangement(new Map());
      }, 600);
    }, 800);
  };

  const animateToArrangementDirect = (newTarget: Map<string, ClassMemberSeatPosition>) => {
    setIsAnimating(true);
    setTargetArrangement(newTarget);
    setAnimationPhase('spread');
    window.setTimeout(() => {
      setSeatArrangement(newTarget);
      setAnimationPhase('none');
      setIsAnimating(false);
      setTargetArrangement(new Map());
    }, 600);
  };

  const handleReset = () => {
    if (!window.confirm('자리 배치를 초기화하시겠습니까?')) return;
    setSeatArrangement(new Map());
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
    clearPick();
  };

  const handleRandomArrangement = () => {
    const placedPositions = Array.from(seatArrangement.values());
    if (placedPositions.length === 0) {
      window.alert('배치된 멤버가 없습니다.');
      return;
    }
    setBeforeRandomArrangement(new Map(seatArrangement));
    const shuffledIds = Array.from(seatArrangement.keys());
    for (let i = shuffledIds.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIds[i], shuffledIds[j]] = [shuffledIds[j]!, shuffledIds[i]!];
    }
    const newArrangement = new Map<string, ClassMemberSeatPosition>();
    shuffledIds.forEach((studentId, index) => {
      newArrangement.set(studentId, placedPositions[index]!);
    });
    clearPick();
    animateToArrangement(newArrangement);
    setIsRandomized(true);
  };

  const handleRestoreArrangement = () => {
    clearPick();
    animateToArrangementDirect(new Map(beforeRandomArrangement));
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
  };

  const handleSelectPreset = (preset: ClassMemberSeatLayoutPreset) => {
    if (isAnimating || editPresetId) return;
    clearPick();
    setSelectedPresetId(preset.id);
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
    const next = seatsRecordToMap(preset.seats);
    if (mapsEqual(next, seatArrangement)) return;
    skipPresetSeatSyncRef.current = true;
    setSeatArrangement(next);
  };

  const handleApplySelectedPreset = () => {
    if (isAnimating) return;
    const selected =
      presetsState.presets.find((p) => p.id === selectedPresetId) ?? presetsState.presets[0];
    if (!selected) return;
    const seats = seatsMapToRecord(seatArrangement);
    const nextPresets = presetsState.presets.map((p) =>
      p.id === selected.id ? { ...p, seats, updatedAt: Date.now() } : p
    );
    persistPresets({
      ...presetsState,
      appliedPresetId: selected.id,
      presets: nextPresets,
    });
  };

  const handleOpenCreateModal = () => {
    setCreateName('');
    setCreateCloneApplied(true);
    setCreateModalOpen(true);
  };

  const handleConfirmCreatePreset = () => {
    const name = createName.trim() || `자리배치 ${presetsState.presets.length}`;
    const appliedSeats =
      presetsState.presets.find((p) => p.id === presetsState.appliedPresetId)?.seats ?? {};
    const seats = createCloneApplied ? { ...appliedSeats } : {};
    const created: ClassMemberSeatLayoutPreset = {
      id: createSeatLayoutPresetId(),
      name,
      isDefault: false,
      seats,
      updatedAt: Date.now(),
    };
    const next = persistPresets({
      ...presetsState,
      presets: [...presetsState.presets, created],
    });
    setCreateModalOpen(false);
    setCreateName('');
    setSelectedPresetId(created.id);
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
    skipPresetSeatSyncRef.current = true;
    animateToArrangementDirect(seatsRecordToMap(created.seats));
    void next;
  };

  const handleOpenEditPreset = (preset: ClassMemberSeatLayoutPreset, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditPresetId(preset.id);
    setEditNameDraft(preset.name);
    setDeleteConfirmPresetId(null);
  };

  const handleCloseEditPreset = () => {
    setEditPresetId(null);
    setEditNameDraft('');
    setDeleteConfirmPresetId(null);
  };

  const handleSaveEditPresetName = () => {
    if (!editPresetId) return;
    const preset = presetsState.presets.find((p) => p.id === editPresetId);
    if (!preset) {
      handleCloseEditPreset();
      return;
    }
    const nextName = editNameDraft.trim();
    const name =
      nextName ||
      (preset.isDefault ? CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_NAME : preset.name);
    persistPresets({
      ...presetsState,
      presets: presetsState.presets.map((p) =>
        p.id === editPresetId ? { ...p, name, updatedAt: Date.now() } : p
      ),
    });
    handleCloseEditPreset();
  };

  const handleRequestDeletePreset = () => {
    if (!editPresetId) return;
    const preset = presetsState.presets.find((p) => p.id === editPresetId);
    if (!preset || preset.isDefault || preset.id === CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID) {
      return;
    }
    setDeleteConfirmPresetId(preset.id);
  };

  const handleConfirmDeletePreset = () => {
    const presetId = deleteConfirmPresetId ?? editPresetId;
    if (!presetId) return;
    const preset = presetsState.presets.find((p) => p.id === presetId);
    if (!preset || preset.isDefault || preset.id === CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID) {
      handleCloseEditPreset();
      return;
    }
    const nextPresets = presetsState.presets.filter((p) => p.id !== preset.id);
    const appliedPresetId =
      presetsState.appliedPresetId === preset.id
        ? CLASS_MEMBER_SEAT_LAYOUT_DEFAULT_PRESET_ID
        : presetsState.appliedPresetId;
    const next = persistPresets({ ...presetsState, presets: nextPresets, appliedPresetId });
    handleCloseEditPreset();
    if (selectedPresetId === preset.id) {
      const fallback =
        next.presets.find((p) => p.id === next.appliedPresetId) ?? next.presets[0]!;
      setSelectedPresetId(fallback.id);
      skipPresetSeatSyncRef.current = true;
      animateToArrangementDirect(seatsRecordToMap(fallback.seats));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const updated = members.map((member) => {
        const position = seatArrangement.get(member.id);
        if (position) {
          return { ...member, seatPosition: position };
        }
        if (member.seatPosition == null) return member;
        const { seatPosition: _cleared, ...rest } = member;
        return rest;
      });
      const seats = seatsMapToRecord(seatArrangement);
      persistPresets({
        ...presetsState,
        appliedPresetId: selectedPresetId,
        presets: presetsState.presets.map((p) =>
          p.id === selectedPresetId ? { ...p, seats, updatedAt: Date.now() } : p
        ),
      });
      await onSave(updated);
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '자리 배치 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const unassignedStudents = members.filter((member) => !seatArrangement.has(member.id));
  const pickedMember = pickedMemberId
    ? members.find((member) => member.id === pickedMemberId) ?? null
    : null;
  const pickedNumber = pickedMember ? numberById.get(pickedMember.id) ?? 0 : 0;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (deleteConfirmPresetId) {
          setDeleteConfirmPresetId(null);
          return;
        }
        if (editPresetId) {
          handleCloseEditPreset();
          return;
        }
        if (createModalOpen) {
          setCreateModalOpen(false);
          return;
        }
        if (pickedMemberId) {
          clearPick();
          return;
        }
        if (showCancelConfirm) {
          setShowCancelConfirm(false);
          return;
        }
        handleCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCancel depends on hasChanges
  }, [
    open,
    pickedMemberId,
    clearPick,
    showCancelConfirm,
    hasChanges,
    createModalOpen,
    editPresetId,
    deleteConfirmPresetId,
  ]);

  if (!open) return null;

  return createPortal(
    <div className="cp-class-seat-modal-root" role="presentation">
      <div className="cp-class-seat-modal-dim" aria-hidden onClick={handleCancel} />
      <div
        className="cp-class-seat-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-class-seat-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-class-seat-modal-header">
          <h2 id="cp-class-seat-modal-title" className="cp-class-seat-modal-title">
            클래스 자리 배정
            {groupName.trim() ? (
              <span className="cp-class-seat-modal-group-label"> · {groupName.trim()}</span>
            ) : null}
          </h2>
          <button
            type="button"
            className="cp-class-seat-modal-close"
            onClick={handleCancel}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        <div className="cp-class-seat-modal-body">
          <div className="cp-class-seat-section" ref={sectionRef}>
            <aside className="cp-class-seat-layout-col">
              <div className="cp-class-seat-layout-cards" role="list" aria-label="자리배치 카드">
                {presetsState.presets.map((preset) => {
                  const isSelected = selectedPresetId === preset.id;
                  const isApplied = presetsState.appliedPresetId === preset.id;
                  return (
                    <div
                      key={preset.id}
                      role="listitem"
                      className={`cp-class-seat-layout-card${isSelected ? ' is-selected' : ''}${
                        isApplied ? ' is-applied' : ''
                      }`}
                      onClick={() => handleSelectPreset(preset)}
                    >
                      <button
                        type="button"
                        className="cp-class-seat-layout-card-edit"
                        aria-label={`${preset.name} 수정`}
                        title="수정"
                        onClick={(e) => handleOpenEditPreset(preset, e)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <span className="cp-class-seat-layout-card-name">{preset.name}</span>
                      {isApplied ? (
                        <span className="cp-class-seat-layout-card-badge">적용중</span>
                      ) : null}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="cp-class-seat-layout-card-add"
                  onClick={handleOpenCreateModal}
                  disabled={isAnimating}
                  aria-label="자리배치 카드 추가"
                >
                  <span className="cp-class-seat-layout-card-add-icon" aria-hidden>
                    +
                  </span>
                  <span>추가</span>
                </button>
              </div>
              <section className="cp-class-seat-rail-section is-unassigned" aria-label="미배치 학생">
                <div
                  className={`cp-class-seat-student-list${
                    unassignedStudents.length === 0 ? ' is-complete' : ''
                  }`}
                >
                  {unassignedStudents.length === 0 ? (
                    <p className="cp-class-seat-unassigned-complete">
                      모든 멤버들이 배치 완료되었습니다.
                    </p>
                  ) : null}
                  {unassignedStudents.map((member) => {
                    const num = numberById.get(member.id) ?? 0;
                    const isPicked = pickedMemberId === member.id;
                    return (
                      <button
                        key={member.id}
                        type="button"
                        className={`cp-class-seat-chip${isPicked ? ' is-picked' : ''}${
                          pickedMemberId && !isPicked ? ' is-swap-candidate' : ''
                        }`}
                        onClick={() => handleMemberClick(member.id)}
                      >
                        <span className="cp-class-seat-chip-num">
                          {String(num).padStart(2, '0')}번
                        </span>
                        <span className="cp-class-seat-chip-name">
                          {displayClassMemberName(member).trim() || '이름 없음'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </aside>

            <div className="cp-class-seat-main-col">
            <div className="cp-class-seat-section-header">
              <div className="cp-class-seat-button-group is-start">
                {isRandomized ? (
                  <button
                    type="button"
                    className="cp-class-seat-tool-btn"
                    onClick={handleRestoreArrangement}
                    disabled={isAnimating}
                  >
                    원래 자리로 복구
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cp-class-seat-tool-btn"
                    onClick={handleRandomArrangement}
                    disabled={isAnimating}
                  >
                    랜덤 자리 배치
                  </button>
                )}
              </div>

              <button
                type="button"
                className={`cp-class-seat-rotate-btn${viewRotated ? ' is-on' : ''}`}
                onClick={() => {
                  clearPick();
                  setViewRotated((prev) => !prev);
                }}
                aria-pressed={viewRotated}
                title="180도 회전해서 보기"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M20 12a8 8 0 0 1-13.7 5.6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4 12a8 8 0 0 1 13.7-5.6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M16 4h2.5v2.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 20H5.5V17.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>180°</span>
              </button>

              <div className="cp-class-seat-button-group">
                <button
                  type="button"
                  className="cp-class-seat-tool-btn"
                  onClick={handleReset}
                  disabled={isAnimating}
                >
                  초기화
                </button>
                <button
                  type="button"
                  className="cp-class-seat-tool-btn is-primary"
                  onClick={handleApplySelectedPreset}
                  disabled={isAnimating}
                  title={
                    appliedPreset
                      ? `선택한 카드를 적용 기준으로 둡니다 (현재 적용: ${appliedPreset.name})`
                      : undefined
                  }
                >
                  적용하기
                </button>
              </div>
            </div>

            <div className="cp-class-seat-grid-slot">
            <div
              className={`cp-class-seat-grid${pickedMemberId ? ' is-placing' : ''}`}
              ref={gridRef}
              onMouseMove={handleGridMouseMove}
              onMouseLeave={() => {
                setPreviewPosition(null);
                setHoverSwapId(null);
              }}
              onClick={handleGridClick}
            >
              <div className="cp-class-seat-grid-bg" aria-hidden>
                {Array.from({ length: GRID_ROWS * GRID_COLS }).map((_, i) => (
                  <div key={i} className="cp-class-seat-grid-cell" />
                ))}
              </div>

              {previewPosition && pickedMember && !hoverSwapId ? (
                <div
                  className="cp-class-seat-preview-block"
                  style={{
                    top: `calc(${
                      (viewRotated ? rotateSeatPosition(previewPosition) : previewPosition).row
                    } * var(--cp-seat-cell-height))`,
                    left: `calc(${
                      (viewRotated ? rotateSeatPosition(previewPosition) : previewPosition).col
                    } * var(--cp-seat-cell-width))`,
                  }}
                >
                  <div className="student-number">{String(pickedNumber).padStart(2, '0')}번</div>
                  <div className="student-name">
                    {displayClassMemberName(pickedMember).trim() || '이름 없음'}
                  </div>
                </div>
              ) : null}

              {Array.from(seatArrangement.entries()).map(([studentId, position]) => {
                const member = members.find((m) => m.id === studentId);
                if (!member) return null;
                let displayPosition = position;
                if (isAnimating) {
                  if (animationPhase === 'center') {
                    displayPosition = getGridCenter();
                  } else if (animationPhase === 'spread') {
                    displayPosition = targetArrangement.get(studentId) || position;
                  }
                }
                if (viewRotated) displayPosition = rotateSeatPosition(displayPosition);
                const isPicked = pickedMemberId === studentId;
                const isSwapTarget = hoverSwapId === studentId;
                const num = numberById.get(member.id) ?? 0;
                return (
                  <button
                    key={studentId}
                    type="button"
                    className={`cp-class-seat-placed-block${isPicked ? ' is-picked' : ''}${
                      isSwapTarget ? ' is-swap-target' : ''
                    }${pickedMemberId && !isPicked ? ' is-swap-candidate' : ''}${
                      isAnimating ? ' is-animating' : ''
                    }${animationPhase === 'center' ? ' phase-center' : ''}${
                      animationPhase === 'spread' ? ' phase-spread' : ''
                    }`}
                    style={{
                      top: `calc(${displayPosition.row} * var(--cp-seat-cell-height))`,
                      left: `calc(${displayPosition.col} * var(--cp-seat-cell-width))`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMemberClick(member.id);
                    }}
                  >
                    <div className="student-number">{String(num).padStart(2, '0')}번</div>
                    <div className="student-name">{displayClassMemberName(member).trim() || '이름 없음'}</div>
                  </button>
                );
              })}
            </div>
            </div>
            </div>
          </div>
        </div>

        {saveError ? <p className="cp-class-seat-modal-error">{saveError}</p> : null}

        <div className="cp-class-seat-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={handleCancel}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => void handleSave()}
            disabled={isSaving || isAnimating}
          >
            {isSaving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>

      {createModalOpen ? (
        <div className="cp-class-seat-nested-modal" role="presentation">
          <div
            className="cp-class-seat-nested-modal-dim"
            aria-hidden
            onClick={() => setCreateModalOpen(false)}
          />
          <div
            className="cp-pin-confirm cp-class-seat-preset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-class-seat-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-class-seat-create-title" className="cp-class-seat-preset-modal-title">
              자리배치 카드 만들기
            </p>
            <label className="cp-class-seat-create-field">
              <span className="cp-class-seat-create-label">이름</span>
              <input
                ref={createNameInputRef}
                className="cp-class-seat-create-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="예: 모둠형, 시험형"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmCreatePreset();
                  }
                }}
              />
            </label>
            <label className="cp-class-seat-create-check">
              <input
                type="checkbox"
                checked={createCloneApplied}
                onChange={(e) => setCreateCloneApplied(e.target.checked)}
              />
              <span>
                적용된 자리배치 복제하기
                {appliedPreset ? ` (현재: ${appliedPreset.name})` : ''}
              </span>
            </label>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setCreateModalOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                onClick={handleConfirmCreatePreset}
              >
                생성
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingPreset && !deleteConfirmPresetId ? (
        <div className="cp-class-seat-nested-modal" role="presentation">
          <div
            className="cp-class-seat-nested-modal-dim"
            aria-hidden
            onClick={handleCloseEditPreset}
          />
          <div
            className="cp-pin-confirm cp-class-seat-preset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-class-seat-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-class-seat-edit-title" className="cp-class-seat-preset-modal-title">
              자리배치 카드 수정
            </p>
            <label className="cp-class-seat-create-field">
              <span className="cp-class-seat-create-label">이름</span>
              <input
                ref={editNameInputRef}
                className="cp-class-seat-create-input"
                value={editNameDraft}
                onChange={(e) => setEditNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveEditPresetName();
                  }
                }}
              />
            </label>
            {!editingPreset.isDefault ? (
              <div className="cp-class-seat-preset-delete-row">
                <button
                  type="button"
                  className="cp-class-seat-icon-btn"
                  aria-label="자리배치 카드 삭제"
                  title="삭제"
                  onClick={handleRequestDeletePreset}
                >
                  <img src="/Trash.svg" alt="" width={16} height={16} />
                </button>
              </div>
            ) : (
              <p className="cp-class-seat-preset-edit-hint">기본 카드는 삭제할 수 없습니다.</p>
            )}
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={handleCloseEditPreset}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                onClick={handleSaveEditPresetName}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirmPresetId && editingPreset ? (
        <div className="cp-class-seat-nested-modal" role="presentation">
          <div
            className="cp-class-seat-nested-modal-dim"
            aria-hidden
            onClick={() => setDeleteConfirmPresetId(null)}
          />
          <div
            className="cp-pin-confirm cp-pin-confirm--warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-class-seat-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-pin-confirm-warn-icon" aria-hidden>
              !
            </div>
            <p id="cp-class-seat-delete-title" className="cp-pin-confirm-warn-title">
              자리배치 카드 삭제
            </p>
            <p className="cp-pin-confirm-warn-desc">
              「{editingPreset.name}」 카드를 삭제할까요?
              <br />
              삭제한 배치는 되돌릴 수 없습니다.
            </p>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setDeleteConfirmPresetId(null)}
              >
                취소
              </button>
              <button type="button" className="cp-apn-btn cp-apn-btn-danger" onClick={handleConfirmDeletePreset}>
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCancelConfirm ? (
        <div className="cp-class-seat-nested-modal" role="presentation">
          <div
            className="cp-class-seat-nested-modal-dim"
            aria-hidden
            onClick={() => setShowCancelConfirm(false)}
          />
          <div
            className="cp-pin-confirm"
            role="dialog"
            aria-modal="true"
            aria-label="저장 안 함 확인"
            onClick={(e) => e.stopPropagation()}
          >
            <p>변경사항이 있습니다. 저장하지 않고 닫으시겠습니까?</p>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                계속 편집
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                onClick={() => {
                  setShowCancelConfirm(false);
                  onClose();
                }}
              >
                저장 안 함
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default ClassMemberSeatAssignmentModal;
